import React, { useRef, useState, useEffect, Fragment } from "react";
import { Trans } from "@lingui/macro";
import Cropper from "react-cropper";
import PropTypes from "prop-types";

import { i18n } from "base/i18n";
import { Button, BUTTON_TYPES, BUTTON_SIZES } from "widgets/inputs";
import { Icon } from "widgets/datadisplay";
import { Tooltip } from "widgets/feedback";
import { Modal } from "widgets/modal";
import { Spinner } from "widgets/spinner";

import { getShortcutText } from "@/common/filepicker/utils/picker.utils";
import { FileToolTip } from "../file_tooltip";
import {
  KEY_CODE,
  KEYDOWN,
  DONE,
  NEXT,
  SVG,
  SHORT_CUT,
  MIME_TYPES_MAP
} from "@/common/filepicker/filepicker.constant";
import { ZoomOutIcon, ZoomInIcon, RotateIcon } from "@/common/icons/index";
import { FileWrapper } from "@/common/filepicker/utils/filewrapper";

import styles from "./style.css";
import "@/common/styles/reactcropper.override.css";

let savedPreviewFiles = {};
const IMAGE_QUALITY = 0.95;

export const CropperModal = (props) => {
  const {
    onModalClose,
    isShowModal,
    selectedFiles,
    config,
    baseURL,
    uploadURL,
    onFileChoosen,
    isLocal,
    prefix
  } = props;

  const [isCropApplied, applyCrop] = useState(false);
  const [previewFiles, updatePreviewFiles] = useState();
  const [isPreview, setPreview] = useState(false);
  const [isOptionChanges, isSetOptionChanges] = useState(true);
  const cropperRef = useRef();

  useEffect(function keyDown() {
    document.addEventListener(KEYDOWN, handleKeyDown);
    return () => {
      document.removeEventListener(KEYDOWN, handleKeyDown);
    };
  }, []);

  useEffect(
    function init() {
      if (isLocal) {
        displayImage();
      } else {
        updatePreviewFiles(selectedFiles);
      }
      setPreview(false);
      applyCrop(false);
      return () => {
        savedPreviewFiles = {};
        updatePreviewFiles({});
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFiles, isLocal]
  );

  let isCrop = config.imageProps?.crop || config?.isCropMandatory;
  const onImageLoad = () => {
    if (
      !isCropApplied &&
      isCrop &&
      selectedFiles?.fileExtension !== SVG &&
      !Object.keys(savedPreviewFiles).length
    ) {
      cropImage();
      cropperRef.current.cropper?.reset();
    }
  };

  const setDragMode = () => {
    let canvasElem = document.querySelector(".cropper-canvas");
    let previewImage = document.querySelector(`.${styles.previewImage}`);
    if (
      canvasElem.offsetWidth > previewImage.offsetWidth ||
      canvasElem.offsetHeight > previewImage.offsetHeight
    ) {
      cropperRef.current.cropper?.setDragMode("move");
    } else {
      cropperRef.current.cropper?.setDragMode("none");
    }
  };

  const applyCropChanges = () => {
    if (selectedFiles.fileExtension !== SVG) {
      let canvas = cropperRef.current.cropper?.getCroppedCanvas();
      let previewImage = document.querySelector(`.${styles.previewImage}`);
      let previewImageWidth = previewImage.getBoundingClientRect().width;
      let previewImageHeight = previewImage.getBoundingClientRect().height;

      savedPreviewFiles = {
        id: previewFiles.id,
        name: previewFiles.name,
        type: previewFiles.type,
        src: canvas.toDataURL(),
        width:
          previewImageWidth < canvas.width ? previewImageWidth : canvas.width,
        height:
          previewImageHeight < canvas.height
            ? previewImageHeight
            : canvas.height
      };
      updatePreviewFiles(savedPreviewFiles);
    }

    applyCrop(true);
    setPreview(true);
  };

  const cropImage = () => {
    cropperRef.current.cropper?.crop();
    if (isCrop) {
      cropperRef.current.cropper?.setAspectRatio(
        config?.imageProps.aspectRatio
      );
    }
    applyCrop(false);
  };

  const rotate = () => {
    cropperRef.current.cropper?.rotate(90);
    if (!isCrop) {
      cropperRef.current.cropper?.clear();
    }
    applyCrop(false);
    isSetOptionChanges(false);
  };

  const zoomInImage = () => {
    cropperRef.current.cropper?.zoom(0.1);
    if (!isCrop) {
      cropperRef.current.cropper?.clear();
    }
    setDragMode();
    isSetOptionChanges(false);
  };

  const zoomOutImage = () => {
    cropperRef.current.cropper?.zoom(-0.1);
    if (!isCrop) {
      cropperRef.current.cropper?.clear();
    }
    setDragMode();
    isSetOptionChanges(false);
  };

  const onReset = () => {
    savedPreviewFiles = {};
    cropImage();
    cropperRef.current.cropper?.zoom(0);
    cropperRef.current.cropper?.rotate(0);
    cropperRef.current.cropper?.reset();
    isSetOptionChanges(true);
    setPreview(false);
    if (isLocal) {
      displayImage();
    } else {
      updatePreviewFiles(selectedFiles);
    }
  };

  const displayImage = () => {
    updatePreviewFiles({
      src: URL.createObjectURL(selectedFiles),
      name: selectedFiles.name,
      type: selectedFiles.type,
      size: selectedFiles.size
    });
  };

  const onPreviewDone = async () => {
    config.validSelectedFile = [];
    let mimeType =
      previewFiles.type || MIME_TYPES_MAP[selectedFiles.fileExtension];
    if (selectedFiles.fileExtension === SVG) {
      onFileUpload(selectedFiles);
    } else {
      let fileobj, canvas;
      canvas = cropperRef.current.cropper?.getCroppedCanvas();
      canvas.toBlob(
        (blob) => {
          fileobj = new File([blob], previewFiles.name, { type: mimeType });
          onFileUpload(fileobj);
        },
        mimeType,
        IMAGE_QUALITY
      );
    }
  };

  const onFileUpload = (fileobj) => {
    let listOfFilesWrapper = [];
    let upload = new FileWrapper(fileobj, baseURL, uploadURL, config, prefix);
    upload.getSelectedFiles();
    listOfFilesWrapper.push(upload);
    onModalClose();
    setPreview(false);
    applyCrop(false);
    updatePreviewFiles({});
    onFileChoosen(listOfFilesWrapper);
  };

  const handleKeyDown = (e) => {
    e = e || window.event;
    if (e.metaKey) {
      if (e.keyCode === KEY_CODE.PLUS) {
        e.preventDefault();
        zoomInImage();
      } else if (e.keyCode === KEY_CODE.MINUS) {
        e.preventDefault();
        zoomOutImage();
      } else if (e.keyCode === KEY_CODE.KEY_R) {
        e.preventDefault();
        rotate();
      }
    } else if (e.keyCode === KEY_CODE.KEY_R) {
      e.preventDefault();
      onReset();
    }
  };

  const modalFooter = () => {
    return (
      <div className={styles.modalFooterWrapper}>
        {isPreview ? (
          <Button
            type={BUTTON_TYPES.SECONDARY}
            size={BUTTON_SIZES.MEDIUM}
            onClick={onReset}
          >
            <Trans>Back</Trans>
          </Button>
        ) : null}
        <Button
          type={BUTTON_TYPES.PRIMARY}
          size={BUTTON_SIZES.MEDIUM}
          onClick={isPreview ? onPreviewDone : applyCropChanges}
          disabled={!(previewFiles && Object.keys(previewFiles).length)}
        >
          <div>{isPreview ? i18n._(DONE) : i18n._(NEXT)}</div>
        </Button>
      </div>
    );
  };

  const modalHeader = () => {
    return (
      <div className={styles.cropperHeaderWrapper}>
        <div className={`ellipsis ${styles.modalTitle}`}>
          <FileToolTip customClass="base">{previewFiles?.name}</FileToolTip>
        </div>
        {!isPreview && selectedFiles.fileExtension !== SVG ? (
          <div className={styles.headerOptionsWrapper}>
            <Tooltip content={getShortcutText(SHORT_CUT.ROTATE)}>
              <Icon onClick={rotate} className={`${styles.hoverStyle}`}>
                <RotateIcon />
              </Icon>
            </Tooltip>
            <div className={styles.zoomContainer}>
              <Tooltip content={getShortcutText(SHORT_CUT.ZOOMIN)}>
                <Icon
                  onClick={zoomInImage}
                  className={`${styles.ZoomIn} ${styles.hoverStyle}`}
                >
                  <ZoomInIcon />
                </Icon>
              </Tooltip>
              <Tooltip content={getShortcutText(SHORT_CUT.ZOOMOUT)}>
                <Icon onClick={zoomOutImage} className={`${styles.hoverStyle}`}>
                  <ZoomOutIcon />
                </Icon>
              </Tooltip>
            </div>
            <Tooltip content={getShortcutText(SHORT_CUT.RESET)}>
              <span
                onClick={onReset}
                className={`smallplus ${
                  isOptionChanges ? styles.resetDisabled : styles.resetOption
                }`}
              >
                <Trans>Reset</Trans>
              </span>
            </Tooltip>
          </div>
        ) : null}
        <div />
      </div>
    );
  };

  const onModalCancel = () => {
    onModalClose();
    updatePreviewFiles({});
    config.validSelectedFile = [];
  };

  return (
    <div>
      <Modal
        titleRenderer={modalHeader}
        footerRenderer={modalFooter}
        open={isShowModal}
        getContainer={() => document.getElementById("filepickercropper")}
        onClose={onModalCancel}
      >
        {previewFiles && Object.keys(previewFiles).length ? (
          <div
            className={`${styles.previewImage} ${styles.previewModalContent} ${
              isPreview && styles.imagePreview
            }`}
          >
            {selectedFiles.fileExtension !== SVG ? (
              <Fragment>
                <Cropper
                  src={previewFiles?.src}
                  className={styles.customCropper}
                  style={{
                    height:
                      isCropApplied && previewFiles.height
                        ? previewFiles.height
                        : "100%",
                    width:
                      isCropApplied && previewFiles.width
                        ? previewFiles.width
                        : "100%"
                  }}
                  initialAspectRatio={16 / 9}
                  guides={false}
                  ref={cropperRef}
                  autoCrop={false}
                  preview=".img-preview"
                  dragCrop={false}
                  dragMode="none"
                  viewMode={1}
                  zoom={true}
                  ready={onImageLoad}
                  responsive={true}
                />
              </Fragment>
            ) : (
              <Fragment>
                {previewFiles && (
                  <img
                    className={styles.svgPreview}
                    src={previewFiles.src}
                    alt="thumbnail"
                  />
                )}
              </Fragment>
            )}
          </div>
        ) : (
          <div className={styles.previewLoader}>
            <Spinner />
          </div>
        )}
      </Modal>
      <div id="filepickercropper" className={styles.filepickercropper} />
    </div>
  );
};

CropperModal.propTypes = {
  onModalClose: PropTypes.func,
  isShowModal: PropTypes.bool,
  selectedFiles: PropTypes.array,
  config: PropTypes.object,
  baseURL: PropTypes.string,
  uploadURL: PropTypes.string,
  onFileChoosen: PropTypes.func,
  isLocal: PropTypes.bool,
  prefix: PropTypes.string
};
